import ShoppingCartService from "./shopping-cart";
import { clearClientCart, updateCart } from "../store/slices/cart/cartSlice";
import { ClientCartItemType, SyncProductType } from "../types/cart";
import TryCart from "./try-cart";
import { clearClientTryCart, updateTryCart } from "../store/slices/try-cart/tryCartSlice";
import axiosNext from "@/api/axios-next";
import axiosInstance from "@/api/axios";
import { getCartThunk } from "../store/slices/cart/cartThunk";
import { getTryCartThunk } from "../store/slices/try-cart/tryCartThunk";
import type { AppDispatch } from "@v2/lib/redux/store";

export type OtpType = "login" | "register";

class LoginService {
	input: string | null = null;

	constructor(input?: string) {
		this.input = input ?? null;
	}

	public async getOtp(type: OtpType) {
		if (!type) throw new Error("type is required");

		const res = await axiosNext({
			url: `/api/auth-2/get-otp`,
			method: "POST",
			data: {
				mobile: this.input,
				type,
			},
		});

		return res.data;
	}

	/**
	 * Reads the guest carts straight from localStorage. A page that runs
	 * on mount cannot rely on Redux here: Bootstrap loads the guest carts
	 * only after the session is known.
	 */
	public static storedClientCartItems(): SyncProductType[] {
		const cart = readStoredJson("client_cart");
		return (cart?.items ?? []).map((item: ClientCartItemType) => ({
			product_id: item.product.product_id,
			quantity: item.qty,
		}));
	}

	public static storedClientTryCartItems(): ClientCartItemType[] {
		return readStoredJson("client_try_cart") ?? [];
	}

	// The dispatch must come from the page, so the carts land in the store
	// the header reads.
	public async syncClientCartAndServer(items: SyncProductType[], dispatch: AppDispatch) {
		const cart = new ShoppingCartService();
		try {
			await cart.clear();

			const { data: cartData } = await cart.addBulk(items);
			dispatch(updateCart(cartData));
		} catch (e) {
			console.error(e);
			dispatch(getCartThunk());
		} finally {
			// Cleared even when the sync fails. A logged-in user never reads
			// the guest cart again, and a leftover copy shows its count in
			// the header on every page load.
			dispatch(clearClientCart());
		}
	}

	public async syncClientTryCartAndServer(items: ClientCartItemType[], dispatch: AppDispatch) {
		const cart = new TryCart();
		try {
			await cart.clear();
			const { data: cartData } = await cart.addBulk(
				items.map((x) => ({
					product_id: x.product.product_id,
				}))
			);

			dispatch(updateTryCart(cartData));
		} catch (e) {
			console.error(e);
			dispatch(getTryCartThunk());
		} finally {
			dispatch(clearClientTryCart());
		}
	}

	public async confirmOtp(code: string) {
		const res = await axiosNext({
			url: `/api/auth-2/sign-in/otp`,
			method: "POST",
			data: {
				mobile: this.input,
				code,
			},
		});
		return res.data;
	}

	public async byCredential(password: string) {
		const res = await axiosNext({
			url: `/api/auth-2/sign-in/password`,
			method: "POST",
			data: {
				email: this.input,
				password,
			},
		});
        
		return res.data;
	}

	public async forgetPassword() {
		const res = await axiosInstance.post(`/auth/forget_password`, {
			email: this.input,
			callback: process.env.NEXT_PUBLIC_APP_URL + "/fa/reset-password",
		});
		return res.data;
	}

	public async register(code: string) {
		const res = await axiosNext({
			url: `/api/auth-2/register`,
			method: "POST",
			data: {
				mobile: this.input,
				code,
			},
		});
		return res.data;
	}

	public async checkExistence() {
		const res = await axiosInstance.post(`/auth/checkExistence`, {
			identity: this.input,
		});
		return res.data;
	}
	async changeMobile() {
		const res = await axiosInstance.put(`/user/change_mobile`, {
			mobile: this.input,
		});
		return res.data;
	}
	async confirmChangeMobile(code: string) {
		const res = await axiosInstance.post(`/user/change_mobile/confirm`, {
			mobile: this.input,
			otp: code,
		});
		return res.data;
	}
}

function readStoredJson(key: string) {
	try {
		const value = localStorage.getItem(key);
		if (!value || value === "undefined" || value === "null") return null;
		return JSON.parse(value);
	} catch {
		return null;
	}
}

export default LoginService;
